import type { KeyboardEvent, RefObject } from 'react'
import React, { useCallback, useEffect } from 'react'

import { ContentEditable } from '@/components/content-editable'

import { cn } from '@/utils'

import { isAtStart, isAtEnd, placeCaret } from '@/shared/lib/selection-caret'

import { HEADING_COLUMN_ID } from '@/entities/rundown'

import type { KeyboardNavigationFocusPayload } from '@/features/keyboard-navigation-handler'
import {
  keyboardNavigationEventBus,
  KeyboardNavigationEvent,
  KeyboardNavigationDirection,
  KeyboardNavigationInput,
} from '@/features/keyboard-navigation-handler'
import { useCueId } from '@/features/rundown/cue/cue.context'

export type CueTitleProps = {
  value?: string
  onChange?: (value?: string) => void | Promise<void>
  disabled?: boolean
  readonly?: boolean
  className?: string
  inputRef: RefObject<HTMLDivElement | null>
  // Used to move focus to the subtitle on ArrowDown from end of title
  subtitleRef?: RefObject<HTMLDivElement | null>
  // Optional external keydown handler to override or pre-handle navigation
  onKeyDown?: (event: KeyboardEvent<HTMLDivElement>) => void
}

export const CueTitle: React.FC<CueTitleProps> = ({
  value,
  onChange,
  disabled = false,
  readonly = false,
  className,
  inputRef,
  subtitleRef,
  onKeyDown: externalOnKeyDown,
}) => {
  const cueId = useCueId()

  const onKeyDown = useCallback(
    (event: KeyboardEvent<HTMLDivElement>) => {
      // Allow external handlers (e.g., Group Title) to pre-handle and override
      externalOnKeyDown?.(event)
      if (event.defaultPrevented) return

      const el = inputRef?.current

      if (!el) return

      const emit = (direction: KeyboardNavigationDirection) => {
        keyboardNavigationEventBus.emit(KeyboardNavigationEvent.JUMP, {
          direction,
          fromCueId: cueId,
          fromColumnId: HEADING_COLUMN_ID,
          fromInputType: KeyboardNavigationInput.TITLE,
        })
      }

      if (event.key === 'Tab') {
        emit(
          event.shiftKey
            ? KeyboardNavigationDirection.REVERSE
            : KeyboardNavigationDirection.FORWARD,
        )
        event.preventDefault()
        return
      }

      if (event.key === 'ArrowLeft' && isAtStart(el)) {
        emit(KeyboardNavigationDirection.LEFT)
        event.preventDefault()
        return
      }

      if (
        event.key === 'ArrowRight' &&
        (isAtEnd(el) || el.innerText.trim().length === 0)
      ) {
        emit(KeyboardNavigationDirection.RIGHT)
        event.preventDefault()
        return
      }

      if (event.key === 'ArrowUp' && isAtStart(el)) {
        emit(KeyboardNavigationDirection.UP)
        event.preventDefault()
        return
      }

      if (
        event.key === 'ArrowDown' &&
        (isAtEnd(el) || el.innerText.trim().length === 0)
      ) {
        const subEl = subtitleRef?.current
        if (subEl) {
          placeCaret(subEl, 'start')
        } else {
          emit(KeyboardNavigationDirection.DOWN)
        }
        event.preventDefault()
        return
      }
    },
    [cueId, inputRef, subtitleRef, externalOnKeyDown],
  )

  // Listen for focusing the header, and place caret in the title unless subtitle is requested
  useEffect(() => {
    const onFocusHeader = ({
      cueId: focusedCueId,
      columnId,
      focusPosition,
      inputType,
    }: KeyboardNavigationFocusPayload) => {
      if (cueId !== focusedCueId || columnId !== HEADING_COLUMN_ID) return
      // If the focus target is subtitle, let subtitle component handle it
      if (inputType === KeyboardNavigationInput.SUBTITLE) return
      const el = inputRef.current
      if (!el) return
      placeCaret(el, focusPosition === 'start' ? 'start' : 'end')
    }

    keyboardNavigationEventBus.on(KeyboardNavigationEvent.FOCUS, onFocusHeader)
    return () =>
      keyboardNavigationEventBus.off(
        KeyboardNavigationEvent.FOCUS,
        onFocusHeader,
      )
  }, [cueId, inputRef])

  return (
    <div className={cn('leading-6', className)}>
      <ContentEditable
        ref={inputRef}
        onKeyDown={onKeyDown}
        className="w-full"
        value={value}
        placeholder={readonly ? '' : 'Title'}
        onChange={onChange}
        disabled={disabled || readonly}
      />
    </div>
  )
}
